import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MobileDropdown from '@/components/MobileDropdown';
import { Plus, Trash2, Edit2, X, Camera, ChefHat, Briefcase, Share2, MessageCircle, Mail, Loader2, ScanLine, Check, Square, CheckSquare, Smartphone } from 'lucide-react';


const CATEGORIES = [
  { value: 'Entradas', label: 'Entradas' }, { value: 'Platos Fuertes', label: 'Platos Fuertes' },
  { value: 'Postres', label: 'Postres' }, { value: 'Bebidas', label: 'Bebidas' },
  { value: 'Guarniciones', label: 'Guarniciones' }, { value: 'Sopas', label: 'Sopas' },
  { value: 'Ensaladas', label: 'Ensaladas' },
];
const UNITS = [
  { value: 'kg', label: 'kg' }, { value: 'g', label: 'g' }, { value: 'lb', label: 'lb' },
  { value: 'oz', label: 'oz' }, { value: 'L', label: 'L' }, { value: 'ml', label: 'ml' },
  { value: 'unidad', label: 'unidad' }, { value: 'pza', label: 'pza' },
  { value: 'taza', label: 'taza' }, { value: 'cda', label: 'cda' }, { value: 'cdta', label: 'cdta' },
];

export default function Recipes() {
  const { token, user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [shareMenu, setShareMenu] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [selectedIngredients, setSelectedIngredients] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState('');
  const [previewRecipe, setPreviewRecipe] = useState(null);
  const scanInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', category: '', portions: 1, selling_price: '', instructions: '', ingredients: [] });
  const currency = user?.currency || 'USD';
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [rRes, iRes] = await Promise.all([
        fetch(`${API_URL}/api/recipes`, { headers }),
        fetch(`${API_URL}/api/ingredients`, { headers })
      ]);
      const r = await rRes.json();
      const i = await iRes.json();
      setRecipes(Array.isArray(r) ? r : []);
      setIngredients(Array.isArray(i) ? i : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  
  useEffect(() => { if (token) load(); }, [token]);

  const resetForm = () => {
    setForm({ name: '', category: '', portions: 1, selling_price: '', instructions: '', ingredients: [] });
    setEditItem(null); setShowForm(false);
  };

  const addIngredientLine = () => setForm(p => ({ ...p, ingredients: [...p.ingredients, { ingredient_id: '', quantity: '', unit: '' }] }));
  const updateIngLine = (idx, field, val) => {
    setForm(p => {
      const ings = [...p.ingredients];
      ings[idx] = { ...ings[idx], [field]: val };
      if (field === 'ingredient_id') {
        const found = ingredients.find(i => i.id === val);
        if (found) ings[idx].unit = found.unit;
      }
      return { ...p, ingredients: ings };
    });
  };
  const removeIngLine = (idx) => setForm(p => ({ ...p, ingredients: p.ingredients.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      category: form.category,
      portions: parseInt(form.portions) || 1, 
      selling_price: parseFloat(form.selling_price) || 0,
      instructions: form.instructions || '',
      ingredients: form.ingredients.map(i => ({ 
        ingredient_id: i.ingredient_id, 
        quantity: parseFloat(i.quantity) || 0, 
        unit: i.unit 
      }))
    };
    try {
      const url = editItem ? `${API_URL}/api/recipes/${editItem.id}` : `${API_URL}/api/recipes`;
      const method = editItem ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        let errMsg = 'Error al guardar';
        try {
          const err = await res.json();
          errMsg = err.detail || errMsg;
        } catch (parseErr) {
          errMsg = `Error ${res.status}: ${res.statusText}`;
        }
        throw new Error(errMsg);
      }
      resetForm(); load();
    } catch (err) { 
      console.error('Recipe submit error:', err);
      alert(err.message || 'Error al guardar. Verifica tu conexion e intenta de nuevo.'); 
    }
  };

  const handleEdit = (r) => {
    setForm({ name: r.name, category: r.category || '', portions: r.portions || 1, selling_price: String(r.selling_price || ''), instructions: r.instructions || '',
      ingredients: (r.ingredients || []).map(i => ({ ingredient_id: i.ingredient_id, quantity: String(i.quantity), unit: i.unit }))
    });
    setEditItem(r); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar receta?')) return;
    await fetch(`${API_URL}/api/recipes/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    load();
  };

  const handleRecipeScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanning(true);
    setScanResults(null);
    setSelectedIngredients({});
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await fetch(`${API_URL}/api/recipes/scan-recipe`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: reader.result })
        });
        const data = await res.json();
        setScanResults(data);
        const preSelected = {};
        data.ingredients?.forEach((_, idx) => { preSelected[idx] = true; });
        setSelectedIngredients(preSelected);
      } catch (err) {
        alert('Error al escanear receta');
        console.error(err);
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const toggleIngredientSelection = (idx) => {
    setSelectedIngredients(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const selectAllIngredients = () => {
    const all = {};
    scanResults?.ingredients?.forEach((_, idx) => { all[idx] = true; });
    setSelectedIngredients(all);
  };

  const deselectAllIngredients = () => {
    setSelectedIngredients({});
  };

  const importScannedRecipe = () => {
    if (!scanResults) return;
    const selectedIngs = scanResults.ingredients?.filter((_, idx) => selectedIngredients[idx]) || [];
    const mappedIngs = selectedIngs.map(si => {
      const match = ingredients.find(i => i.name.toLowerCase().includes(si.name.toLowerCase()) || si.name.toLowerCase().includes(i.name.toLowerCase()));
      return {
        ingredient_id: match?.id || '',
        quantity: String(si.quantity || ''),
        unit: si.unit || match?.unit || 'g',
        scanned_name: si.name,
        confidence: si.confidence || 1
      };
    });
    setForm({
      name: scanResults.name || '',
      category: scanResults.category || '',
      portions: scanResults.servings || 1,
      selling_price: '',
      instructions: (scanResults.instructions || []).join('\n'),
      ingredients: mappedIngs
    });
    setShowForm(true);
    setScanResults(null);
    setSelectedIngredients({});
  };

  const handlePhotoUpload = async (recipeId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await fetch(`${API_URL}/api/recipes/${recipeId}/photo`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ photo: reader.result })
        });
        load();
      } catch { alert('Error subiendo foto'); }
    };
    reader.readAsDataURL(file);
  };

  const downloadPDF = async (id, name, type) => {
    const pdfId = `${id}_${type}`;
    setDownloadingPdf(pdfId);
    try {
      const endpoint = type === 'kitchen' ? `/api/reports/recipe/${id}/kitchen-pdf` : `/api/reports/recipe/${id}/pdf`;
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `M3_${name}_${type === 'kitchen' ? 'Cocina' : 'Gerencia'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Error al descargar PDF');
    } finally {
      setDownloadingPdf('');
    }
  };

  const shareVia = async (type, recipeId, recipeName) => {
    try {
      setDownloadingPdf('share_' + recipeId);
      const response = await fetch(`${API_URL}/api/reports/recipe/${recipeId}/pdf`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al generar PDF');
      const blob = await response.blob();
      const fileName = `M3_${recipeName.replace(/\s+/g, '_')}_Ficha.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      
      if (type === 'whatsapp' || type === 'email') {
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: `M3 - Ficha Tecnica: ${recipeName}`,
              text: `Te comparto la ficha tecnica de "${recipeName}" - M3 Gastronomic`,
              files: [file]
            });
            setShareMenu(null);
            return;
          } catch (shareErr) {
            console.log('Web Share cancelled or failed:', shareErr);
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        if (type === 'whatsapp') {
          setTimeout(() => {
            const text = encodeURIComponent(`M3 GASTRONOMIC\n\nFicha Tecnica: ${recipeName}\n\nAdjunta el PDF que se acaba de descargar.\n\nGenerado con M3 Gastronomic`);
            window.open(`https://wa.me/?text=${text}`, '_blank');
          }, 500);
        } else if (type === 'email') {
          setTimeout(() => {
            const subject = encodeURIComponent(`M3 - Ficha Tecnica: ${recipeName}`);
            const body = encodeURIComponent(`Hola,\n\nTe comparto la ficha tecnica de "${recipeName}".\n\nAdjunta el archivo PDF que se descargo.\n\n--\nM3 Gastronomic`);
            window.location.href = `mailto:?subject=${subject}&body=${body}`;
          }, 500);
        }
      }
    } catch (err) {
      console.error('Share error:', err);
      alert('Error al compartir. Intenta descargar el PDF manualmente.');
    } finally {
      setDownloadingPdf('');
      setShareMenu(null);
    }
  };

  const selectedIngCount = Object.values(selectedIngredients).filter(Boolean).length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recipes-page">
      {!showForm && (<>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Recetas</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Fichas Tecnicas</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="file" ref={scanInputRef} accept="image/*" onChange={handleRecipeScan} className="hidden" />
          <button onClick={() => scanInputRef.current?.click()} disabled={scanning} data-testid="scan-recipe-btn"
            className="flex items-center gap-1.5 px-3 py-2 border border-gold/40 text-gold text-[10px] sm:text-xs font-bold uppercase tracking-widest hover:bg-gold/10 transition-colors disabled:opacity-50">
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />}
            <span className="hidden sm:inline">{scanning ? 'Escaneando...' : 'Escanear Receta'}</span>
            <span className="sm:hidden">{scanning ? '...' : 'Escanear'}</span>
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-recipe-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-gold text-black text-[10px] sm:text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors">
            <Plus size={14} /> Nueva Receta
          </button>
        </div>
      </div>

      {/* Scanned Recipe Results */}
      {scanResults && (
        <div className="bg-gold/5 border border-gold/20 p-6" data-testid="recipe-scan-results">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gold text-sm font-semibold">Receta Escaneada</p>
              <p className="text-xs text-white/40">Selecciona los datos que deseas importar</p>
            </div>
            <button onClick={() => { setScanResults(null); setSelectedIngredients({}); }} className="text-white/30 hover:text-white"><X size={16} /></button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-white/5 border border-white/10">
            <div>
              <p className="text-[10px] uppercase text-white/40 mb-1">Nombre</p>
              <p className="text-sm font-medium">{scanResults.name || 'Sin nombre'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-white/40 mb-1">Categoria</p>
              <p className="text-sm">{scanResults.category || 'Sin categoria'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-white/40 mb-1">Porciones</p>
              <p className="text-sm">{scanResults.servings || 'No especificado'}</p>
            </div>
          </div>

          {scanResults.ingredients?.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase text-white/40">Ingredientes Detectados ({scanResults.ingredients?.length || 0})</p>
                <div className="flex items-center gap-3">
                  <button onClick={selectAllIngredients} className="text-xs text-gold hover:underline">Seleccionar todos</button>
                  <button onClick={deselectAllIngredients} className="text-xs text-white/40 hover:text-white">Deseleccionar</button>
                  <span className="text-xs text-white/40">{selectedIngCount} seleccionados</span>
                </div>
              </div>
              
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scanResults.ingredients?.map((ing, i) => (
                  <div 
                    key={i} 
                    onClick={() => toggleIngredientSelection(i)}
                    className={`flex items-center gap-3 py-3 px-4 border cursor-pointer transition-all ${
                      selectedIngredients[i] 
                        ? 'border-gold/50 bg-gold/10' 
                        : ing.confidence >= 0.8 
                          ? 'border-emerald-400/30 bg-emerald-400/5' 
                          : ing.confidence >= 0.5 
                            ? 'border-amber-400/30 bg-amber-400/5' 
                            : 'border-red-400/30 bg-red-400/5'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {selectedIngredients[i] ? <CheckSquare size={18} className="text-gold" /> : <Square size={18} className="text-white/30" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{ing.name}</span>
                        {ing.confidence < 0.8 && (
                          <span className={`text-[10px] px-1.5 py-0.5 ${ing.confidence >= 0.5 ? 'bg-amber-400/20 text-amber-400' : 'bg-red-400/20 text-red-400'}`}>
                            {ing.confidence >= 0.5 ? 'Revisar' : 'Dudoso'}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-white/40">{ing.quantity} {ing.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {scanResults.instructions?.length > 0 && (
            <div className="mb-4 p-4 bg-white/5 border border-white/10">
              <p className="text-[10px] uppercase text-white/40 mb-2">Instrucciones</p>
              <div className="text-xs text-white/60 space-y-1 max-h-32 overflow-y-auto">
                {scanResults.instructions.slice(0, 5).map((step, i) => <p key={i}>{i + 1}. {step}</p>)}
                {scanResults.instructions.length > 5 && <p className="text-white/30">+{scanResults.instructions.length - 5} pasos mas...</p>}
              </div>
            </div>
          )}

          {scanResults.notes && (
            <div className="mb-4 p-3 bg-amber-400/5 border border-amber-400/20">
              <p className="text-[10px] uppercase text-amber-400/60 mb-1">Notas del AI</p>
              <p className="text-xs text-white/60">{scanResults.notes}</p>
            </div>
          )}

          <button onClick={importScannedRecipe} disabled={selectedIngCount === 0 && !scanResults.name} data-testid="import-scanned-recipe"
            className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            <Check size={14} />
            Crear Receta {selectedIngCount > 0 ? `con ${selectedIngCount} Ingrediente${selectedIngCount !== 1 ? 's' : ''}` : ''}
          </button>
        </div>
      )}

      {/* Recipe Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="recipes-grid">
        {recipes.map((r, i) => (
          <div key={r.id} className="bg-[#0A0A0A] border border-white/5 hover:border-gold/20 transition-colors p-6" data-testid={`recipe-card-${i}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                {r.photo ? (
                  <img src={r.photo.startsWith('data:') ? r.photo : `data:image/jpeg;base64,${r.photo}`} alt="" className="w-16 h-16 object-cover border border-white/10" />
                ) : (
                  <div className="w-16 h-16 bg-white/5 border border-white/10 flex items-center justify-center">
                    <Camera size={16} className="text-white/20" />
                  </div>
                )}
                <div>
                  <h4 className="text-lg font-semibold">{r.name}</h4>
                  <p className="text-xs text-white/40">{r.category} | {r.portions} porciones</p>
                </div>
              </div>
              <div className="flex gap-1.5">
                <input type="file" accept="image/*" capture="environment" onChange={(e) => handlePhotoUpload(r.id, e)} className="hidden" id={`photo-${r.id}`} />
                <button onClick={() => document.getElementById(`photo-${r.id}`).click()} data-testid={`photo-recipe-${i}`} className="p-1 text-white/30 hover:text-gold" title="Agregar foto"><Camera size={14} /></button>
                <button 
                  onClick={() => downloadPDF(r.id, r.name, 'kitchen')} 
                  disabled={downloadingPdf === `${r.id}_kitchen`}
                  data-testid={`kitchen-pdf-${i}`} 
                  title="Escandallo Cocina" 
                  className="p-1 text-white/30 hover:text-emerald-400 disabled:opacity-50"
                >
                  {downloadingPdf === `${r.id}_kitchen` ? <Loader2 size={14} className="animate-spin" /> : <ChefHat size={14} />}
                </button>
                <button 
                  onClick={() => downloadPDF(r.id, r.name, 'management')} 
                  disabled={downloadingPdf === `${r.id}_management`}
                  data-testid={`mgmt-pdf-${i}`} 
                  title="Ficha Gerencia" 
                  className="p-1 text-white/30 hover:text-blue-400 disabled:opacity-50"
                >
                  {downloadingPdf === `${r.id}_management` ? <Loader2 size={14} className="animate-spin" /> : <Briefcase size={14} />}
                </button>
                <div className="relative">
                  <button onClick={() => setShareMenu(shareMenu === r.id ? null : r.id)} data-testid={`share-recipe-${i}`} className="p-1 text-white/30 hover:text-gold"><Share2 size={14} /></button>
                  {shareMenu === r.id && (
                    <div className="absolute right-0 top-7 z-20 bg-[#0A0A0A] border border-white/10 py-1 min-w-[140px] shadow-lg">
                      <button onClick={() => shareVia('whatsapp', r.id, r.name)} className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2"><MessageCircle size={12} className="text-emerald-400" /> WhatsApp</button>
                      <button onClick={() => shareVia('email', r.id, r.name)} className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2"><Mail size={12} className="text-blue-400" /> Email</button>
                    </div>
                  )}
                </div>
                <button onClick={() => setPreviewRecipe(previewRecipe?.id === r.id ? null : r)} data-testid={`preview-recipe-${i}`} className="p-1 text-white/30 hover:text-purple-400" title="Vista previa movil"><Smartphone size={14} /></button>
                <button onClick={() => handleEdit(r)} data-testid={`edit-recipe-${i}`} className="p-1 text-white/30 hover:text-gold"><Edit2 size={14} /></button>
                <button onClick={() => handleDelete(r.id)} data-testid={`delete-recipe-${i}`} className="p-1 text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Costo Total</p>
                <p className="text-lg font-mono text-gold">{currency} {r.total_cost?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Costo +5% Inflacion</p>
                <p className="text-lg font-mono text-amber-400">{currency} {r.inflation_adjusted_cost?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Precio Venta</p>
                <p className="text-lg font-mono">{currency} {r.selling_price?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Food Cost</p>
                <p className={`text-lg font-mono ${r.food_cost_pct > 35 ? 'text-red-400' : 'text-emerald-400'}`}>{r.food_cost_pct?.toFixed(1)}%</p>
              </div>
            </div>
            <div className="bg-white/5 p-3 mb-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Precio Sugerido ({r.restaurant_type_label || 'Normal'} x{r.multiplier || 3})</p>
              <p className="text-gold font-mono text-lg font-bold">{currency} {r.suggested_price?.toFixed(2)}</p>
              <p className="text-[10px] text-white/30 mt-1">Basado en costo +5% inflacion x multiplicador tipo restaurante</p>
            </div>
            {r.ingredients?.length > 0 && (
              <div className="pt-3 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{r.ingredients.length} ingredientes</p>
                {r.ingredients.slice(0, 3).map((ing, j) => (
                  <div key={j} className="flex justify-between text-xs text-white/50"><span>{ing.name}</span><span>{ing.quantity} {ing.unit}</span></div>
                ))}
                {r.ingredients.length > 3 && <p className="text-xs text-white/30">+{r.ingredients.length - 3} mas</p>}
              </div>
            )}
          </div>
        ))}
      </div>
      {recipes.length === 0 && !showForm && <p className="text-center text-white/30 py-12 text-sm">No hay recetas</p>}

      {/* Mobile Preview */}
      {previewRecipe && (
        <div className="mt-6" data-testid="mobile-preview-section">
          <div className="flex items-center gap-3 mb-4">
            <Smartphone size={16} className="text-purple-400" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Vista Previa Movil</p>
            <button onClick={() => setPreviewRecipe(null)} className="ml-auto text-white/30 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex justify-center">
            {/* Phone Frame */}
            <div style={{ width: 375, maxWidth: '100%' }}>
              <div className="border-2 border-white/20 rounded-[2rem] p-2 bg-black shadow-2xl">
                {/* Notch */}
                <div className="flex justify-center mb-1">
                  <div className="w-24 h-5 bg-black rounded-b-2xl border-b border-x border-white/10" />
                </div>
                {/* Screen Content */}
                <div className="bg-[#0A0A0A] rounded-[1.2rem] overflow-hidden" style={{ height: 640, overflowY: 'auto' }}>
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gold/20">
                      <div className="w-6 h-6 bg-gold/20 rounded flex items-center justify-center">
                        <span className="text-gold text-[10px] font-bold">M3</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-gold/60">Ficha Tecnica</span>
                    </div>

                    {/* Photo */}
                    {previewRecipe.photo ? (
                      <img src={previewRecipe.photo.startsWith('data:') ? previewRecipe.photo : `data:image/jpeg;base64,${previewRecipe.photo}`} alt="" className="w-full h-40 object-cover rounded mb-4" />
                    ) : (
                      <div className="w-full h-28 bg-white/5 rounded mb-4 flex items-center justify-center">
                        <Camera size={28} className="text-white/10" />
                      </div>
                    )}

                    {/* Name & Category */}
                    <h3 className="text-lg font-bold text-white mb-1">{previewRecipe.name}</h3>
                    <p className="text-xs text-white/40 mb-4">{previewRecipe.category} | {previewRecipe.portions} porciones</p>

                    {/* Price Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-white/5 rounded p-3">
                        <p className="text-[9px] uppercase text-white/30 mb-1">Costo</p>
                        <p className="text-sm font-mono text-gold">{currency} {previewRecipe.total_cost?.toFixed(2)}</p>
                      </div>
                      <div className="bg-white/5 rounded p-3">
                        <p className="text-[9px] uppercase text-white/30 mb-1">+5% Inflacion</p>
                        <p className="text-sm font-mono text-amber-400">{currency} {previewRecipe.inflation_adjusted_cost?.toFixed(2)}</p>
                      </div>
                      <div className="bg-white/5 rounded p-3">
                        <p className="text-[9px] uppercase text-white/30 mb-1">Precio Venta</p>
                        <p className="text-sm font-mono">{currency} {previewRecipe.selling_price?.toFixed(2)}</p>
                      </div>
                      <div className="bg-white/5 rounded p-3">
                        <p className="text-[9px] uppercase text-white/30 mb-1">Food Cost</p>
                        <p className={`text-sm font-mono ${previewRecipe.food_cost_pct > 35 ? 'text-red-400' : 'text-emerald-400'}`}>{previewRecipe.food_cost_pct?.toFixed(1)}%</p>
                      </div>
                    </div>

                    {/* Suggested Price */}
                    <div className="bg-gold/10 border border-gold/20 rounded p-3 mb-4 text-center">
                      <p className="text-[9px] uppercase text-gold/50 mb-1">Precio Sugerido ({previewRecipe.restaurant_type_label || 'Normal'} x{previewRecipe.multiplier || 3})</p>
                      <p className="text-xl font-mono font-bold text-gold">{currency} {previewRecipe.suggested_price?.toFixed(2)}</p>
                    </div>

                    {/* Ingredients */}
                    {previewRecipe.ingredients?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2">{previewRecipe.ingredients.length} Ingredientes</p>
                        <div className="space-y-1">
                          {previewRecipe.ingredients.map((ing, j) => (
                            <div key={j} className="flex justify-between text-xs py-1 border-b border-white/5 last:border-0">
                              <span className="text-white/60">{ing.name}</span>
                              <span className="text-white/40 font-mono">{ing.quantity} {ing.unit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Instructions */}
                    {previewRecipe.instructions && (
                      <div className="mb-4">
                        <p className="text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2">Instrucciones</p>
                        <p className="text-xs text-white/50 leading-relaxed whitespace-pre-line">{previewRecipe.instructions}</p>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="text-center pt-3 border-t border-white/5">
                      <p className="text-[8px] uppercase tracking-[0.3em] text-white/15">M3 Gastronomic Intelligence</p>
                    </div>
                  </div>
                </div>
                {/* Home Bar */}
                <div className="flex justify-center mt-2">
                  <div className="w-28 h-1 bg-white/20 rounded-full" />
                </div>
              </div>
            </div>
          </div>
          {/* Share buttons below phone */}
          <div className="flex justify-center gap-3 mt-4">
            <button onClick={() => shareVia('whatsapp', previewRecipe.id, previewRecipe.name)} data-testid="preview-share-whatsapp"
              className="flex items-center gap-2 px-4 py-2 border border-emerald-400/30 text-emerald-400 text-xs hover:bg-emerald-400/10 transition-colors">
              <MessageCircle size={14} /> Compartir WhatsApp
            </button>
            <button onClick={() => shareVia('email', previewRecipe.id, previewRecipe.name)} data-testid="preview-share-email"
              className="flex items-center gap-2 px-4 py-2 border border-blue-400/30 text-blue-400 text-xs hover:bg-blue-400/10 transition-colors">
              <Mail size={14} /> Enviar Email
            </button>
            <button onClick={() => downloadPDF(previewRecipe.id, previewRecipe.name, 'management')} data-testid="preview-download-pdf"
              className="flex items-center gap-2 px-4 py-2 border border-gold/30 text-gold text-xs hover:bg-gold/10 transition-colors">
              <Briefcase size={14} /> Descargar PDF
            </button>
          </div>
        </div>
      )}
      </>)}

      {/* Recipe Form - inline, no fixed overlay */}
      {showForm && (
        <div data-testid="recipe-form-modal">
          <div className="flex items-center gap-3 mb-6">
            <button type="button" onClick={resetForm} data-testid="close-recipe-form" className="text-white/40 hover:text-white text-sm flex items-center gap-1">
              <X size={14} /> Volver a Recetas
            </button>
            <h3 className="font-heading text-xl font-bold">{editItem ? 'Editar' : 'Nueva'} Receta</h3>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Nombre *</label>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="recipe-name-input"
                    className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Categoria</label>
                  <MobileDropdown
                    options={CATEGORIES}
                    value={form.category}
                    onChange={v => setForm(p => ({ ...p, category: v }))}
                    testId="recipe-category-select"
                    placeholder="Seleccionar..."
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Porciones</label>
                  <input type="number" min="1" value={form.portions} onChange={e => setForm(p => ({ ...p, portions: e.target.value }))} data-testid="recipe-portions-input"
                    className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Precio Venta ({currency})</label>
                  <input type="number" step="0.01" value={form.selling_price} onChange={e => setForm(p => ({ ...p, selling_price: e.target.value }))} data-testid="recipe-price-input"
                    className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" />
                </div>
              </div>
              <div className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Ingredientes</label>
                  <button type="button" onClick={addIngredientLine} data-testid="add-recipe-ingredient-btn" className="text-xs text-gold hover:underline">+ Agregar</button>
                </div>
                {form.ingredients.map((line, idx) => (
                  <div key={idx} className="flex items-end gap-2 mb-3">
                    <div className="flex-1">
                      {line.scanned_name && !line.ingredient_id && (
                        <p className="text-[10px] text-amber-400 mb-1">Escaneado: {line.scanned_name}</p>
                      )}
                      <label className="text-[10px] text-white/30 mb-1 block">Ingrediente</label>
                      <MobileDropdown
                        options={ingredients.map(i => ({ value: i.id, label: `${i.name} (${currency} ${i.price}/${i.unit})` }))}
                        value={line.ingredient_id}
                        onChange={v => updateIngLine(idx, 'ingredient_id', v)}
                        testId={`recipe-ing-select-${idx}`}
                        placeholder="Seleccionar..."
                      />
                    </div>
                    <div className="w-20">
                      <label className="text-[10px] text-white/30 mb-1 block">Cantidad</label>
                      <input type="number" step="0.01" placeholder="0" value={line.quantity} onChange={e => updateIngLine(idx, 'quantity', e.target.value)} data-testid={`recipe-ing-qty-${idx}`}
                        className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none text-center" />
                    </div>
                    <div className="w-24">
                      <label className="text-[10px] text-white/30 mb-1 block">Unidad</label>
                      <MobileDropdown
                        options={UNITS}
                        value={line.unit}
                        onChange={v => updateIngLine(idx, 'unit', v)}
                        testId={`recipe-ing-unit-${idx}`}
                        placeholder="Unidad"
                      />
                    </div>
                    <button type="button" onClick={() => removeIngLine(idx)} className="text-white/30 hover:text-red-400 pb-2"><X size={14} /></button>
                  </div>
                ))}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Instrucciones</label>
                <textarea value={form.instructions} onChange={e => setForm(p => ({ ...p, instructions: e.target.value }))} rows={3} data-testid="recipe-instructions-input"
                  className="w-full bg-transparent border border-white/10 py-2 px-3 text-sm focus:border-gold focus:outline-none resize-none" />
              </div>
              <button type="submit" data-testid="save-recipe-btn" className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors mt-4">
                {editItem ? 'Actualizar' : 'Guardar'} Receta
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
